// "Set Pickup Location" — assign a warehouse to many Not-Shipped / Booking-Failed orders at once.
// The list is the merchant's OWN warehouses plus the shared ShipRoute master warehouses, exactly what the
// Create-Order pickup dropdown offers. Options come from the server so the picker can never offer
// something the server would reject.
import { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Loader2, Warehouse, AlertTriangle, Search } from 'lucide-react';
import { api } from '@/api/client';
import { Modal } from '@/components/ui/Modal';

interface Props {
  ids: string[];
  onClose: () => void;
  onDone: () => void;
}

export function AssignWarehouseDialog({ ids, onClose, onDone }: Props) {
  const [warehouseId, setWarehouseId] = useState<number | ''>('');
  const [q, setQ] = useState('');

  const { data, isLoading, error } = useQuery({
    queryKey: ['warehouse-options', ids],
    queryFn: async () => (await api.post('/orders/warehouse-options', { ids })).data as {
      merchantId: number; orderCount: number;
      notEditable: { id: string; orderNo: string; statusId: number }[];
      warehouses: { id: number; name: string; city?: string; state?: string; pincode?: string; contactName?: string; isMaster: boolean; group: string }[];
    },
  });

  const save = useMutation({
    mutationFn: async () => (await api.post('/orders/bulk/assign-warehouse', { ids, warehouseId })).data,
    onSuccess: (r: any) => {
      toast.success(`Pickup location set to "${r.warehouseName}" on ${r.updated} order(s)`);
      onDone();
      onClose();
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Could not set the pickup location'),
  });

  const all = data?.warehouses ?? [];
  const blocked = data?.notEditable ?? [];
  const serverMsg = (error as any)?.response?.data?.message;

  // Group headings keep "your warehouses" visually separate from the shared master ones.
  const groups = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const match = needle
      ? all.filter((w) => [w.name, w.city, w.state, w.pincode].some((v) => String(v ?? '').toLowerCase().includes(needle)))
      : all;
    const out: Record<string, typeof all> = {};
    for (const w of match) (out[w.group] ??= []).push(w);
    return out;
  }, [all, q]);
  const matchCount = Object.values(groups).reduce((s, g) => s + g.length, 0);

  return (
    <Modal isOpen onClose={onClose} title="Set Pickup Location" subtitle="Your warehouses and the ShipRoute master warehouses" maxWidth="lg">
      <div className="space-y-4">
        {isLoading ? (
          <div className="flex items-center gap-2 text-sm text-slate-500 py-6 justify-center">
            <Loader2 size={16} className="animate-spin" /> Loading pickup locations…
          </div>
        ) : serverMsg ? (
          <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg p-3">{serverMsg}</div>
        ) : (
          <>
            <div className="text-sm text-slate-600">
              Updating <b>{data?.orderCount}</b> order{(data?.orderCount ?? 0) === 1 ? '' : 's'}.
            </div>

            {blocked.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 text-amber-800 text-xs rounded-lg p-3 flex gap-2">
                <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                <span>
                  {blocked.length} selected order(s) are already shipped or cancelled and can&apos;t be changed
                  ({blocked.slice(0, 4).map((o) => o.orderNo).join(', ')}{blocked.length > 4 ? '…' : ''}). Deselect them first.
                </span>
              </div>
            )}

            <div className="relative">
              <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name / city / pincode"
                className="w-full border border-slate-300 rounded-lg pl-7 pr-2 py-1.5 text-sm outline-none focus:border-primary" />
            </div>

            {matchCount === 0 ? (
              <div className="bg-slate-50 border border-slate-200 text-slate-600 text-sm rounded-lg p-4 text-center">
                {all.length === 0
                  ? <>No pickup location is available for this account yet.<div className="text-xs text-slate-500 mt-1">Add one under Pickup Locations, or ask an admin for a master warehouse.</div></>
                  : 'No pickup location matches that search.'}
              </div>
            ) : (
              <div className="border border-slate-200 rounded-lg max-h-80 overflow-y-auto">
                {Object.entries(groups).map(([group, list]) => (
                  <div key={group}>
                    <div className="px-3 py-1.5 bg-slate-50 text-[11px] font-semibold text-slate-500 uppercase tracking-wide sticky top-0">
                      {group} ({list.length})
                    </div>
                    {list.map((w) => (
                      <label key={w.id} className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer border-t hover:bg-slate-50 ${warehouseId === w.id ? 'bg-primary/5' : ''}`}>
                        <input type="radio" name="warehouse" checked={warehouseId === w.id} onChange={() => setWarehouseId(w.id)} />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-slate-800 truncate">{w.name}</div>
                          <div className="text-xs text-slate-500 truncate">
                            {[w.city, w.state, w.pincode].filter(Boolean).join(', ') || '—'}
                            {w.contactName ? ` · ${w.contactName}` : ''}
                          </div>
                        </div>
                        <span className="text-[10px] text-slate-400 font-mono">#{w.id}</span>
                      </label>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <button onClick={onClose} className="px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">Cancel</button>
          <button
            onClick={() => save.mutate()}
            disabled={save.isPending || !warehouseId || blocked.length > 0 || all.length === 0}
            title={blocked.length > 0 ? 'Deselect the already-shipped orders first' : !warehouseId ? 'Pick a pickup location' : undefined}
            className="px-4 py-1.5 text-sm bg-primary text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 flex items-center gap-2">
            {save.isPending ? <Loader2 size={14} className="animate-spin" /> : <Warehouse size={14} />}
            {save.isPending ? 'Updating…' : `Update ${ids.length} order(s)`}
          </button>
        </div>
      </div>
    </Modal>
  );
}
